import { Box, DialogContent, Modal } from "@mui/material"
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import _ from "lodash"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Outlet, useParams } from "react-router"
import { usePageviewTracking } from "src/screens/Detail/use-pageview-tracking"
import { s } from "src/utilities/common"

import { DGText } from "../../components/common/DGText"
import { Footer } from "../../components/common/Footer"
import { ProgressSpinner } from "../../components/ProgressSpinner"
import {
  TeaserDimension,
  TeaserDimensions,
  TTeaser,
} from "../../components/Sliders/Slider"
import { Teaser } from "../../components/Sliders/Teaser"
import { MystageSliderId, useBrowseScreen } from "../../hooks/use-browse-screen"
import {
  MyStagePageQuery,
  SearchByFacetValueQuery,
  SearchByTextQuery,
  SliderPageQuery,
} from "../../hooks/use-sdk"
import { useSmoothNavigate } from "../../hooks/use-smooth-navigate"
import { appStore } from "../../store/AppStore"
import { dimensions } from "../../utilities/constants"
import { theme, TI } from "../../utilities/theme"
import { useFocusHelper } from "../../utilities/use-focus-helper"

const Screen = ({
  browseScreen,
}: {
  browseScreen: ReturnType<typeof useBrowseScreen>
}) => {
  const { id } = useParams()

  const { focusSelf, focusKey, ref } = useFocusable({
    isFocusBoundary: true,
    forceFocus: true,
  })

  const teasers = useRef<TTeaser[] | undefined>(null)

  const {
    teasers: teasersState,
    headline,
    columnCount,
    colSpacing,
    dimensionId,
    load,
    spinner,
    data,
  } = browseScreen

  useEffect(() => {
    if (teasers.current && focusSelf) focusSelf()
  }, [focusSelf, teasersState])

  const { addFocus, getFocus, getMetaInfo } = useFocusHelper()
  const track = usePageviewTracking()

  const setSpinner = appStore((state) => state.setSpinner)
  const [metaInfo, setMetaInfo] = useState(0)

  useEffect(() => {
    setSpinner("out")
    const mInfo = getMetaInfo<{ selected: number }>()
    setMetaInfo(mInfo && "selected" in mInfo ? mInfo.selected : 0)
    const trackedSliderId =
      id === MystageSliderId.RecentlyPlayedVideo ||
      id === MystageSliderId.RecentlyPlayedAudio
        ? "my-stage/recent"
        : id === MystageSliderId.FavoriteVideo ||
            id === MystageSliderId.FavoriteAudio
          ? "my-stage/favorites"
          : "search/browse/" + id
    track("GridPage", trackedSliderId)
  }, [])

  useEffect(() => {
    if (data) getFocus()
  }, [data])

  const scrollRef = useRef<HTMLElement>(null)
  const navigate = useSmoothNavigate()

  ///
  const [selected, setSelected] = useState<number>(0)
  useEffect(() => {
    setSelected(metaInfo)
  }, [metaInfo])
  const buffer = 2 //number of rows
  const selectedHelper = useRef(selected)
  const range = useMemo(() => {
    if (teasers.current && selected > teasers.current.length - 20) load()
    if (teasers.current?.length !== teasersState?.length)
      teasers.current = teasersState
    selectedHelper.current = selected
    const rowPos = selected % columnCount
    return [
      Math.max(0, selected - rowPos - columnCount * buffer),
      Math.min(
        teasersState?.length ?? 0,
        selected + columnCount - rowPos + columnCount * buffer,
      ),
    ]
  }, [teasersState, selected])
  ///

  return (
    <Box
      sx={{ width: "100vw", height: "100vh" }}
      className={"loading_in"}
      ref={scrollRef}
    >
      <Box
        sx={{
          //opacity: visible,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pl: s("96px"),
          pr: s("96px"),
        }}
      >
        <DGText
          data-testid={"browsescreen-title"}
          tx={TI.Subheadline02tv}
          sx={{
            mt: s("60px"),
            mb: s("90px"),
            color: theme.palette.white[55],
          }}
        >
          {headline}
        </DGText>
        {/* DGAPP-6473: remove count */}
        {/*<DGText
          tx={TI.Subheadline04tv}
          sx={{
            color: theme.palette.white.main,
            mb: s("90px"),
          }}
        >
          {t("teaserGrid__listing_results", [(totalResults ?? 0).toString()])}
        </DGText>*/}
        <FocusContext.Provider value={focusKey}>
          {/* not using the MUI Grid system any longer because Samsung doesn't support rowGap or columnGap which were introduced in the MUI versions after 5.x */}
          <Box
            component={"div"}
            ref={ref}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              mt: "-40px",
              ml: `-${colSpacing}`,
              width: `calc(100% + ${colSpacing})`,
              boxSizing: "border-box",
            }}
          >
            {_.range(range[0], range[1]).map((i) => {
              const item = teasers.current ? teasers.current[i] : undefined
              if (item)
                return (
                  <Box
                    sx={{ pl: colSpacing, pt: "40px" }}
                    key={"browse-" + item.id + i}
                  >
                    <Teaser
                      onFocus={(index: number) => {
                        // prevent page scroll if the page is short
                        if (
                          ref.current.clientHeight <
                          // add slight padding, to prevent literal "edge" cases
                          dimensions.height - s(50)
                        )
                          return
                        if (scrollRef.current)
                          scrollRef.current.style.transform =
                            "translate3d(0, " +
                            (index + 1 > columnCount * buffer
                              ? -TeaserDimensions[
                                  dimensionId as TeaserDimension
                                ].height - s(550)
                              : index < columnCount
                                ? 0
                                : -TeaserDimensions[
                                    dimensionId as TeaserDimension
                                  ].height) +
                            "px, 0)"
                        setSelected(index)
                      }}
                      onPress={(d?: TTeaser) => {
                        if (!d) return
                        addFocus({
                          meta: {
                            selected: selectedHelper.current,
                            data,
                          },
                        })
                        scrollRef.current?.classList.add("loading_out")
                        navigate(`../detail/${d.type}/${d.id}/0/0/0`)
                      }}
                      scrollDirection={"vertical"}
                      dimensions={dimensionId as TeaserDimension}
                      data={item}
                      me={"browseScreen"}
                      scrollView={ref}
                      index={i}
                      colMargin={false}
                      sliderLayout={browseScreen.sliderLayout}
                    />
                  </Box>
                )
            })}
          </Box>
        </FocusContext.Provider>
      </Box>
      {spinner && <ProgressSpinner />}
      <Footer />
    </Box>
  )
}

export type BrowsePage = Exclude<
  | SliderPageQuery
  | MyStagePageQuery
  | SearchByFacetValueQuery
  | SearchByTextQuery,
  undefined
>

export const BrowseScreen = () => {
  const { id: pageId, cursor } = useParams()
  const id = pageId as MystageSliderId
  const ref = useRef<HTMLElement>(null)
  const browseScreen = useBrowseScreen()

  if (!browseScreen.teasers) return <ProgressSpinner />
  return (
    <>
      <Modal
        key={"browsescreen" + id + cursor}
        open={true}
        sx={{
          height: "100vh",
          width: "100vw",
          backgroundColor: theme.palette.primary.main,
        }}
        hideBackdrop
      >
        <DialogContent
          sx={{
            backgroundColor: theme.palette.primary.main,
            overflowY: "visible",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
          }}
          ref={ref}
        >
          <Screen browseScreen={browseScreen} />
        </DialogContent>
      </Modal>
      <Outlet />
    </>
  )
}
